import React, { useState } from 'react';
import styled from 'styled-components';
import { Grid } from '@mui/material';
import dayjs from 'dayjs';
import { useFormik } from 'formik';
import { useParams } from 'react-router-dom';
import { PAGE_TEXT } from '../lang';
import { updateApplicationItem, type HKPFSInformation } from '../../../graphql';
import {
  Accordion,
  TextField,
  Button,
  Alert,
  type AlertDetails,
  NotificationText,
} from '../../../../../components';
import { handleReadySubmit } from '../../../../../utils';

export type HKPFSProps = {
  HKPFSReferenceNumber: string;
  enableEdit: boolean;
  refetchApplication: () => void;
  handleLoading: (callback: () => Promise<void>) => Promise<void>;
};

const pageText = PAGE_TEXT.FELLOWSHIP.HKPFS;

export const HKPFSForm: React.FunctionComponent<HKPFSProps> = (
  props: HKPFSProps,
) => {
  const {
    HKPFSReferenceNumber,
    enableEdit,
    refetchApplication,
    handleLoading,
  } = props;
  const { applicationId } = useParams();
  const [openAlert, setOpenAlert] = useState(false);
  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'error',
  });

  const HKPFSOverdue = dayjs().isAfter(
    process.env.REACT_APP_SCHOLARSHIP_DEADLINE,
  );
  const disableHKPFS = HKPFSOverdue || !enableEdit;

  const updateHKPFS = updateApplicationItem();
  const handleSubmit = async (values: HKPFSInformation): Promise<void> => {
    await handleLoading(async () => {
      try {
        await updateHKPFS({
          variables: {
            id: applicationId,
            updateId: '', // can input any string here
            input: {
              hkpfsInformation: {
                applicationId,
                ...values,
              },
            },
          },
        });

        refetchApplication();
        setAlertDetails({
          alertContent: 'Saved HKPFS successfully. ',
          alertType: 'success',
        });
      } catch (error: any) {
        setAlertDetails({
          alertContent: error.message,
          alertType: 'error',
        });
      }
    });
    setOpenAlert(true);
  };

  const formik = useFormik<HKPFSInformation>({
    initialValues: {
      HKPFSReferenceNumber,
    },
    onSubmit: handleSubmit,
  });

  return (
    <>
      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />
      <NotificationText note={PAGE_TEXT.FELLOWSHIP.RECOMMEND_PPPFS} />
      <Accordion
        defaultExpanded
        title={pageText.HKPFS_NAME}
        details={
          <Container>
            <form noValidate onSubmit={handleReadySubmit(formik)}>
              <Grid container flexDirection={'column'} spacing={2}>
                <Grid
                  size={{
                    md: 4,
                    xs: 12,
                    sm: 6,
                  }}
                >
                  <TextField
                    name="HKPFSReferenceNumber"
                    placeholder={'PF24-xxxxx'}
                    fullWidth
                    disabled={disableHKPFS}
                    label={pageText.HKPFS_REFERENCE_NUMBER}
                    InputLabelProps={{ shrink: true }}
                    value={formik.values.HKPFSReferenceNumber}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      Boolean(formik.touched.HKPFSReferenceNumber) &&
                      Boolean(formik.errors.HKPFSReferenceNumber)
                    }
                    helperText={
                      Boolean(formik.touched.HKPFSReferenceNumber) &&
                      formik.errors.HKPFSReferenceNumber
                    }
                  />
                </Grid>
                <div className="p-4">
                  <div
                    style={{
                      fontSize: '1rem',
                    }}
                    dangerouslySetInnerHTML={{
                      __html: pageText.APPLY_HKPFS_NOTICE,
                    }}
                  />
                  <div
                    className="mt-2"
                    style={{
                      textDecoration: 'underline',
                    }}
                    dangerouslySetInnerHTML={{
                      __html: pageText.APPLICATION_GUIDE,
                    }}
                  />
                </div>

                {!disableHKPFS && (
                  <Grid
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      margin: '2rem 0 1rem 0',
                    }}
                    size={{
                      md: 12,
                    }}
                  >
                    <Button variant="contained" type="submit">
                      {PAGE_TEXT.SAVE}
                    </Button>
                  </Grid>
                )}
              </Grid>
            </form>
          </Container>
        }
      />
    </>
  );
};

const Container = styled.div`
  margin-top: 35px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;

  form {
    display: flex;
    flex-direction: column;
    justify-content: center;
    margin-bottom: 2rem;
    padding: 0 2rem;
    width: 100%;
  }
`;
